import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { formatEther } from "ethers";
import { toast } from "sonner";
import { getNetworkConfig } from "@/lib/api";
import { executeSell, tradeErrorMessage } from "@/lib/poolSwap";
import {
  TradeHeader, AmountBox, OutputBox, TokenPill, QuoteError, QuoteBreakdown, QuoteRow, QuoteDivider, FeeLabel,
  PoolDetails, SubmitButton, TxLink,
} from "@/components/exchange/TradeParts";
import { Card, CardContent } from "@/components/ui/card";
import { EXCHANGE } from "@/constants/testIds";
import { useWallet } from "@/contexts/WalletContext";
import { useLiveQuote, useTradeExecution } from "@/hooks/useTrade";
import { TrendingDown } from "lucide-react";
import { motion } from "framer-motion";
import { FADE_UP_SLOW } from "@/lib/motion";
import { GasHint, GAS_UNITS } from "@/components/exchange/GasHint";

const GAS_RESERVE_WEI = 10n ** 16n; // 0.01 FMI kept for gas
const MAX_PRECISION_WEI = 10n ** 12n; // floor MAX to 6 decimals

const SellBreakdown = ({ quote, usdtDecimals }) => (
  <QuoteBreakdown>
    <QuoteRow label="FMI Price" value={`$${quote.rate} USD`} />
    <QuoteRow label="USDT Price (INR)" value={`INR ${quote.usdtPriceInr}`} />
    <QuoteRow label="Gross Amount" value={`${quote.grossAmount} USDT`} />
    <QuoteRow label={<FeeLabel percent={quote.feePercent} />} value={`-${quote.feeAmount} USDT`} testId={EXCHANGE.feeAmount} />
    <QuoteRow label="Tax" value={`-${quote.taxUsd} USDT`} testId={EXCHANGE.taxAmount} />
    <QuoteDivider />
    <QuoteRow strong label="You Receive" value={`${quote.outputAmount} USDT`} valueClass="text-[#D4AF37] font-display font-bold" />
    {quote.source === "fsc-pool" && <PoolDetails quote={quote} decimals={usdtDecimals} token="USDT" prefix="sell" />}
  </QuoteBreakdown>
);

const MaxControl = ({ balance, onMax }) => (
  <div className="flex items-center gap-2 text-xs">
    <span data-testid="sell-fmi-balance" className="text-zinc-500">Balance: {balance ?? "—"} FMI</span>
    <button data-testid="sell-max-btn" onClick={onMax}
      className="px-2 py-0.5 rounded-full border border-[#D4AF37]/40 text-[#D4AF37] font-semibold hover:bg-[#D4AF37]/10 transition-colors">
      MAX
    </button>
  </div>
);

export default function SellPage() {
  const [amount, setAmount] = useState("");
  const { address, balance, getProvider, refreshBalances } = useWallet();
  const { data: config } = useQuery({
    queryKey: ["networkConfig"], queryFn: getNetworkConfig,
    refetchInterval: (q) => (q.state.data?.sellRouter?.address ? false : 3000),
  });
  const live = Boolean(config?.sellRouter?.address);
  const { quote, setQuote, quoteError, loading } = useLiveQuote("sell", amount, "FMI", "USDT");
  const trade = useTradeExecution({ address, refreshBalances, onDone: () => { setAmount(""); setQuote(null); } });

  const fillMax = async () => {
    try {
      const wei = BigInt(await getProvider().request({ method: "eth_getBalance", params: [address, "latest"] }));
      const max = wei - GAS_RESERVE_WEI;
      if (max <= 0n) {
        toast.error("Not enough FMI to sell after keeping 0.01 FMI for gas");
        return;
      }
      setAmount(formatEther(max - (max % MAX_PRECISION_WEI)));
    } catch {
      toast.error("Could not read your FMI balance");
    }
  };

  const handleExecute = () =>
    trade.run({
      live: quote?.source === "fsc-pool",
      execute: (onStep) => executeSell({ eip1193: getProvider(), quote, onStep }),
      successMessage: `Sold ${quote?.inputAmount} FMI for ~${quote?.outputAmount} USDT`,
      errorMessage: (err) => tradeErrorMessage(err, "Sale", "the sale and gas"),
      demoMessage: "Sell order executed successfully (Demo)",
    });

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-12">
      <motion.div {...FADE_UP_SLOW}>
        <TradeHeader accent="Sell" title="FMI" subtitle="Sell FMI tokens for USDT" live={live} liveTestId="sell-live-badge" />
        <Card className="border-[#2A2411] bg-[#0A0A0A]/80 backdrop-blur-xl gold-glow">
          <CardContent className="p-5 space-y-4">
            <AmountBox label="You Sell (FMI)" amount={amount} onChange={setAmount} extra={address && <MaxControl balance={balance} onMax={fillMax} />}>
              <TokenPill symbol="FMI" />
            </AmountBox>
            <OutputBox label="You Receive (USDT)" loading={loading} quote={quote}><TokenPill symbol="USDT" /></OutputBox>
            <QuoteError error={quoteError} loading={loading} />
            {quote && <SellBreakdown quote={quote} usdtDecimals={config?.usdt?.decimals ?? 6} />}
            <SubmitButton onClick={handleExecute} disabled={!quote || trade.executing || !amount} executing={trade.executing} step={trade.step}
              address={address} amount={amount} connectLabel="Connect Wallet to Sell">
              <span className="flex items-center gap-2"><TrendingDown size={16} /> {quote?.source === "fsc-pool" ? "Sell FMI" : "Sell FMI (Demo)"}</span>
            </SubmitButton>
            {address && quote?.source === "fsc-pool" && <GasHint gasUnits={GAS_UNITS.sell} note="paid in FMI on top of the amount" testId="sell-gas-hint" />}
            <TxLink hash={trade.lastTx} testId="sell-tx-link" label="View sale transaction" />
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
